import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { MaterialIcon } from "@/components/MaterialIcon";
import { Badge, Field, PageTitle, btnPrimary, btnSecondary, card, inputCls } from "@/components/admin/ui";

const EMPTY = { email: "", password: "", name: "", organizer_for: "", role: "organizer" };

export default function AdminUsers() {
  const { data: users, reload } = useApi("/admin/users");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Account created");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageTitle title="Users">
        <button type="button" className={btnPrimary} onClick={() => setForm(EMPTY)} data-testid="add-user-button">
          <MaterialIcon name="add" size={20} weight={600} />
          Add user
        </button>
      </PageTitle>

      {form && (
        <div className={`${card} mb-6 space-y-4 p-5`} data-testid="new-user-form">
          <p className="text-[15px] font-semibold">New account</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="new-user-email" /></Field>
            <Field label="Temporary password" hint="8+ characters. Share it with them.">
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="new-user-password" />
            </Field>
            <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="new-user-name" /></Field>
            <Field label="Event organizer for"><input value={form.organizer_for} onChange={(e) => setForm({ ...form, organizer_for: e.target.value })} className={inputCls} data-testid="new-user-organizer-for" /></Field>
          </div>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="new-user-role">
              <option value="organizer">Organizer</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={() => setForm(null)} data-testid="cancel-user-button">Cancel</button>
            <button type="button" className={btnPrimary} onClick={create} disabled={busy} data-testid="save-user-button">{busy ? "Creating…" : "Create account"}</button>
          </div>
        </div>
      )}

      <div className={`${card} overflow-hidden`} data-testid="users-table">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-[13px]">
          <thead className="bg-[#F9FAFB] text-left text-[12px] text-[#6B7280]">
            <tr>
              <th className="px-4 py-2.5 font-medium">Account</th>
              <th className="px-4 py-2.5 font-medium">Organizer for</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium text-center">Festivals</th>
              <th className="px-4 py-2.5 font-medium text-center">Passes sold</th>
              <th className="px-4 py-2.5 font-medium">Payouts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {users?.map((u) => (
              <tr key={u.id} className="hover:bg-[#F9FAFB]" data-testid="user-row">
                <td className="px-4 py-2.5">
                  <Link to={`/admin/users/${u.id}`} className="font-medium hover:underline" data-testid={`user-link-${u.id}`}>{u.email}</Link>
                  {!u.active && <span className="ml-2"><Badge tone="red">disabled</Badge></span>}
                </td>
                <td className="px-4 py-2.5 text-[#6B7280]">{u.organizer_for || "—"}</td>
                <td className="px-4 py-2.5"><Badge tone={u.role === "admin" ? "blue" : "gray"}>{u.role}</Badge></td>
                <td className="px-4 py-2.5 text-center">{u.festival_count}</td>
                <td className="px-4 py-2.5 text-center">{u.paid_sales}</td>
                <td className="px-4 py-2.5"><Badge tone={u.stripe_connected ? "green" : "gray"}>{u.stripe_connected ? "linked" : "not linked"}</Badge></td>
              </tr>
            ))}
            {users?.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]">No accounts yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
